// TrademarkFlow.jsx - Simple flow with expandable algorithm tree
import mermaid from "mermaid";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

function MermaidDiagram({ data, query }) {
  const [showAlgorithm, setShowAlgorithm] = useState(false);

  // Skip rendering if no data
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const { sample_results: rawResults = [], grade = "" } = data;
  const sample_results = Array.isArray(rawResults) ? rawResults : [];
  const count = sample_results.length;
  const exactMatches = sample_results.filter(r => r.similarity_to_query >= 1);
  const maxSim = count > 0 ? Math.max(...sample_results.map(r => r.similarity_to_query || 0)) : 0;
  const isHighSim = maxSim >= 0.7;

  // Get reasoning text
  const getReasoning = () => {
    if (count === 0) return "No existing trademarks found - clear to proceed";
    if (exactMatches.length >= 2) return `${exactMatches.length} exact name matches - high risk`;
    if (count >= 6 && maxSim >= 0.7) return `Many conflicts (${count}) with high similarity (${(maxSim * 100).toFixed(0)}%)`;
    if (count >= 6) return `Many conflicts (${count}) but low similarity`;
    if (maxSim >= 0.7) return `Few conflicts but ${(maxSim * 100).toFixed(0)}% max similarity`;
    return `Few conflicts (${count}) with low similarity`;
  };

  // Get grade color classes
  const getGradeClasses = () => {
    switch (grade) {
      case "A": return "bg-success/20 border-success text-success";
      case "B": return "bg-info/20 border-info text-info";
      case "C": return "bg-warning/20 border-warning text-warning";
      case "D": return "bg-error/20 border-error text-error";
      case "F": return "bg-error text-white border-error";
      default: return "bg-base-content/10 border-base-content/30 text-base-content/50";
    }
  };

  return (
    <div className="space-y-3">
      {/* Simple visual flow */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 py-3 flex-wrap">
        {/* Query box - styled like NameTile */}
        <div className="px-3 py-1.5 bg-base-100 border border-base-content/10 rounded-lg">
          <span className="font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555] text-xs sm:text-sm block">
            {query.substring(0, 16)}{query.length > 16 ? "..." : ""}
          </span>
        </div>

        <div className="text-base-content/20 text-xs">→</div>

        {/* Results count */}
        <div className="px-3 py-1.5 bg-base-content/5 rounded-lg text-xs sm:text-sm">
          <span className="font-semibold text-base-content">{count}</span>
          <span className="text-base-content/50 ml-1">results</span>
        </div>

        {maxSim > 0 && (
          <>
            <div className="text-base-content/20 text-xs">→</div>

            {/* Max similarity */}
            <div className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm ${
              isHighSim ? "bg-warning/10 text-warning" : "bg-base-content/5 text-base-content/70"
            }`}>
              <span className="font-semibold">{(maxSim * 100).toFixed(0)}%</span>
              <span className="opacity-70 ml-1">similar</span>
            </div>
          </>
        )}

        <div className="text-base-content/20 text-xs">→</div>

        {/* Grade circle */}
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg font-bold border-2 ${getGradeClasses()}`}>
          {grade || "?"}
        </div>
      </div>

      {/* Reasoning */}
      <p className="text-center text-[11px] text-base-content/40">
        {getReasoning()}
      </p>

      {/* Toggle for algorithm */}
      <button
        onClick={() => setShowAlgorithm(!showAlgorithm)}
        className="flex items-center justify-center gap-1 mx-auto text-[10px] text-base-content/30 hover:text-base-content/50 transition-colors"
      >
        <FontAwesomeIcon icon={showAlgorithm ? faChevronUp : faChevronDown} className="text-[8px]" />
        {showAlgorithm ? "Hide" : "Show"} algorithm
      </button>

      {/* Full algorithm tree */}
      {showAlgorithm && (
        <AlgorithmTree data={data} query={query} />
      )}
    </div>
  );
}

// Separate component for the Mermaid diagram
function AlgorithmTree({ data, query }) {
  const diagramRef = useRef(null);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "base",
      logLevel: "error",
      securityLevel: "loose",
      flowchart: {
        htmlLabels: true,
        curve: "basis",
        useMaxWidth: true,
        diagramPadding: 8,
        nodeSpacing: 20,
        rankSpacing: 30,
      },
      themeVariables: {
        fontFamily: "system-ui, sans-serif",
        fontSize: "10px",
        lineColor: "#e5e5e5",
        primaryColor: "#fff",
        primaryBorderColor: "#ddd",
        primaryTextColor: "#999",
      },
    });

    const renderDiagram = async () => {
      if (diagramRef.current) {
        try {
          const diagram = generateDiagram();
          diagramRef.current.innerHTML = diagram;
          diagramRef.current.removeAttribute("data-processed");
          await mermaid.run({ nodes: [diagramRef.current] });
        } catch (error) {
          console.error("Mermaid error:", error);
        }
      }
    };

    renderDiagram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, query]);

  const generateDiagram = () => {
    const { sample_results: rawResults = [], grade = "" } = data;
    const sample_results = Array.isArray(rawResults) ? rawResults : [];
    const safeQuery = query.replace(/['"<>]/g, "").substring(0, 12);

    const exactMatches = sample_results.filter(r => r.similarity_to_query >= 1);
    const count = sample_results.length;
    const maxSim = count > 0 ? Math.max(...sample_results.map(r => r.similarity_to_query || 0)) : 0;
    const simPct = (maxSim * 100).toFixed(0);

    const noResults = count === 0;
    const hasExact = exactMatches.length >= 2;
    const highCount = count >= 6;
    const highSim = maxSim >= 0.7;

    let activeGrade = grade;
    if (!activeGrade) {
      if (noResults) activeGrade = "A";
      else if (hasExact) activeGrade = "F";
      else if (highCount && highSim) activeGrade = "D";
      else if (highCount && !highSim) activeGrade = "B";
      else if (!highCount && highSim) activeGrade = "C";
      else activeGrade = "A";
    }

    // All muted gray, only active path gets highlight
    const muted = "fill:#fafafa,stroke:#e5e5e5,stroke-width:1px,color:#bbb";
    const active = "fill:#fef7ed,stroke:#c76f31,stroke-width:2px,color:#555";

    const gradeActive = (g) => {
      const isActive = activeGrade === g || (g === "A1" && noResults) || (g === "A2" && activeGrade === "A" && !noResults);
      if (!isActive) return muted;
      const colors = { A: "#22c55e", A1: "#22c55e", A2: "#22c55e", B: "#3b82f6", C: "#f59e0b", D: "#ef4444", F: "#dc2626" };
      const c = colors[g] || "#22c55e";
      return `fill:${c}15,stroke:${c},stroke-width:2px,color:${c}`;
    };

    // Track active path
    const p = ["S", "R"];
    if (!noResults) { p.push("E"); if (!hasExact) { p.push("C"); p.push(highCount ? "SH" : "SL"); } }

    return `graph LR
      S["${safeQuery}"]
      R{"${count}"}
      S --> R
      R -->|None| A1((A))
      R -->|Found| E
      E{"Exact"}
      E -->|"≥2"| F((F))
      E -->|"0-1"| C
      C{"Count"}
      C -->|"<6"| SL
      C -->|"≥6"| SH
      SL{"${simPct}%"}
      SH{"${simPct}%"}
      SL -->|"<70"| A2((A))
      SL -->|"≥70"| GC((C))
      SH -->|"<70"| B((B))
      SH -->|"≥70"| D((D))

      style S ${p.includes("S") ? active : muted}
      style R ${p.includes("R") ? active : muted}
      style E ${p.includes("E") ? active : muted}
      style C ${p.includes("C") ? active : muted}
      style SL ${p.includes("SL") ? active : muted}
      style SH ${p.includes("SH") ? active : muted}
      style A1 ${gradeActive("A1")}
      style A2 ${gradeActive("A2")}
      style B ${gradeActive("B")}
      style GC ${gradeActive("C")}
      style D ${gradeActive("D")}
      style F ${gradeActive("F")}

      linkStyle default stroke:#eee,stroke-width:1px
      ${noResults ? "linkStyle 0,1 stroke:#c76f31,stroke-width:2px" : ""}
      ${!noResults && hasExact ? "linkStyle 0,2,3 stroke:#c76f31,stroke-width:2px" : ""}
      ${!noResults && !hasExact && !highCount && !highSim ? "linkStyle 0,2,4,5,7 stroke:#c76f31,stroke-width:2px" : ""}
      ${!noResults && !hasExact && !highCount && highSim ? "linkStyle 0,2,4,5,8 stroke:#c76f31,stroke-width:2px" : ""}
      ${!noResults && !hasExact && highCount && !highSim ? "linkStyle 0,2,4,6,9 stroke:#c76f31,stroke-width:2px" : ""}
      ${!noResults && !hasExact && highCount && highSim ? "linkStyle 0,2,4,6,10 stroke:#c76f31,stroke-width:2px" : ""}
    `;
  };

  return (
    <div className="mt-3 pt-3 border-t border-base-content/5">
      <div className="mermaid text-center overflow-x-auto" ref={diagramRef} />
    </div>
  );
}

export default MermaidDiagram;
